import { Controller } from "@hotwired/stimulus";

/**
 * Dialog Controller
 *
 * A Stimulus controller that wraps the native <dialog> element with:
 * - Smooth enter/leave transitions via data attributes
 * - Click-outside-to-close behavior
 * - Scroll locking (native dialog behavior)
 * - Focus trapping (native dialog behavior)
 * - Escape key handling
 *
 * State is communicated via data-dialog-state attribute:
 * - "closed"   : Dialog is hidden
 * - "entering" : Dialog is transitioning in
 * - "open"     : Dialog is fully visible
 * - "leaving"  : Dialog is transitioning out
 */
export default class extends Controller {
  static targets = ["dialog", "backdrop", "panel"];
  static values = {
    open: { type: Boolean, default: false },
    cancellable: { type: Boolean, default: true },
  };

  connect() {
    // Bind the cancel event (Escape key)
    this.boundHandleCancel = this.handleCancel.bind(this);
    this.boundHandleTransitionEnd = this.handleTransitionEnd.bind(this);

    this.dialogTarget.addEventListener("cancel", this.boundHandleCancel);
    this.dialogTarget.addEventListener("transitionend", this.boundHandleTransitionEnd);

    // Check initial open state
    if (this.openValue) {
      // Open immediately without transition for initial state
      this.previousActiveElement = document.activeElement;
      this.dialogTarget.showModal();
      this.setState("open");
    } else {
      this.setState("closed");
    }
  }

  disconnect() {
    this.dialogTarget.removeEventListener("cancel", this.boundHandleCancel);
    this.dialogTarget.removeEventListener("transitionend", this.boundHandleTransitionEnd);
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  /**
   * Opens the dialog with enter transition
   * @param {Object} options - Optional settings
   * @param {boolean} options.immediate - Skip transition animation (default: false)
   */
  show(options = {}) {
    if (this.isOpen) return;

    const { immediate = false } = options;

    // Show the native dialog (enables focus trap + top-layer)
    this.dialogTarget.showModal();

    // Store previous active element for focus restoration
    this.previousActiveElement = document.activeElement;

    if (immediate) {
      // Skip transition, go directly to open
      this.setState("open");
    } else {
      // Start in closed state, then transition to entering
      this.setState("closed");

      // Force reflow to ensure closed state is applied before transition
      void this.dialogTarget.offsetHeight;

      // Begin enter transition
      this.setState("entering");
    }

    this.openValue = true;
    this.dispatch("open");
  }

  /**
   * Closes the dialog with leave transition
   * @param {Object} options - Optional settings
   * @param {boolean} options.restoreFocus - Whether to restore focus (default: true)
   */
  hide(options = {}) {
    if (!this.isOpen) return;

    const { restoreFocus = true } = options;
    this.shouldRestoreFocus = restoreFocus;

    // Begin leave transition
    this.setState("leaving");

    this.openValue = false;
    this.dispatch("close");
  }

  /**
   * Toggles the dialog open/closed state
   */
  toggle() {
    this.isOpen ? this.hide() : this.show();
  }

  // ─────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────

  /**
   * Handles clicks on the backdrop/outside the panel
   * @param {Event} event
   */
  clickOutside(event) {
    // Ignore if dialog is not cancellable
    if (!this.cancellableValue) return;

    // Only close if clicking directly on the backdrop, not the panel
    if (this.hasPanelTarget && this.panelTarget.contains(event.target)) {
      return;
    }

    // Dispatch cancel event (can be prevented)
    const cancelEvent = this.dispatch("cancel", { cancelable: true });

    if (!cancelEvent.defaultPrevented) {
      this.hide();
    }
  }

  /**
   * Handles the native dialog cancel event (Escape key)
   * @param {Event} event
   */
  handleCancel(event) {
    // Prevent the native close so we can animate
    event.preventDefault();

    // Ignore if dialog is not cancellable
    if (!this.cancellableValue) return;

    // Dispatch our cancel event (can be prevented)
    const cancelEvent = this.dispatch("cancel", { cancelable: true });

    if (!cancelEvent.defaultPrevented) {
      this.hide();
    }
  }

  /**
   * Handles transition end to finalize state changes
   * @param {TransitionEvent} event
   */
  handleTransitionEnd(event) {
    // Only handle transitions on our direct targets
    if (!this.isOwnTarget(event.target)) return;

    // Only handle opacity transitions to avoid multiple fires
    if (event.propertyName !== "opacity") return;

    const currentState = this.currentState;

    if (currentState === "entering") {
      this.setState("open");
    } else if (currentState === "leaving") {
      this.setState("closed");
      this.dialogTarget.close();

      // Restore focus if needed
      if (this.shouldRestoreFocus !== false && this.previousActiveElement) {
        this.previousActiveElement.focus();
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Value Changed Callbacks
  // ─────────────────────────────────────────────────────────────

  openValueChanged(isOpen, wasOpen) {
    // Skip initial connection
    if (wasOpen === undefined) return;

    if (isOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Private Helpers
  // ─────────────────────────────────────────────────────────────

  /**
   * Sets the dialog state on all relevant elements
   * @param {string} state - One of: closed, entering, open, leaving
   */
  setState(state) {
    const elements = [
      this.element,
      this.dialogTarget,
      this.hasBackdropTarget ? this.backdropTarget : null,
      this.hasPanelTarget ? this.panelTarget : null,
    ].filter(Boolean);

    elements.forEach((el) => {
      el.dataset.dialogState = state;
    });
  }

  /**
   * Gets the current dialog state
   * @returns {string}
   */
  get currentState() {
    return this.element.dataset.dialogState || "closed";
  }

  /**
   * Checks if the dialog is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.currentState === "open" || this.currentState === "entering";
  }

  /**
   * Checks if an element is one of our targets
   * @param {Element} element
   * @returns {boolean}
   */
  isOwnTarget(element) {
    return (
      element === this.dialogTarget ||
      (this.hasBackdropTarget && element === this.backdropTarget) ||
      (this.hasPanelTarget && element === this.panelTarget)
    );
  }
}
